import { DownloadOutlined, EyeOutlined, FileWordOutlined, SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Col, Empty, Row, Space, Tabs, Tag, Tooltip, Typography, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { urlAdmin } from '../../constants';
import { dateCommon } from '../../helpers/common';
import { CauHinhHopDongFile } from '../../models/setting/Setting';
import { layCauHinhHopDong, luuCauHinhHopDong } from '../../services/setting';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';

const { Text } = Typography;

/// Định dạng tệp mẫu hợp đồng được phép tải lên, phải khớp với Constants.DinhDangMauHopDong ở API
const DINH_DANG_CHO_PHEP = ['.docx'];
const DUNG_LUONG_TOI_DA = 20000000; // 20 MB

const CauHinhHopDong = () => {
    const dispatch = useDispatch();
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [fileCauHinh, setFileCauHinh] = useState<CauHinhHopDongFile | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    /// Đường dẫn tương đối được API trả về dạng Windows, đổi sang URL để trình duyệt mở được
    const urlFile = fileCauHinh ? urlAdmin + fileCauHinh.duongDan?.replace(/\\/g, '/') : '';

    const layDuLieu = async () => {
        try {
            dispatch(showLoadingScreen());
            const rs = await layCauHinhHopDong();
            setFileCauHinh(rs);
        } catch (err: any) {
            toast.error('Đã xảy ra lỗi, không lấy được cấu hình mẫu hợp đồng lao động!');
        }
        dispatch(hideLoadingScreen());
    };

    const propsUpload: UploadProps = {
        accept: DINH_DANG_CHO_PHEP.join(','),
        maxCount: 1,
        fileList,
        onRemove: () => {
            setFileList([]);
        },
        beforeUpload: (file) => {
            const dinhDang = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            if (!DINH_DANG_CHO_PHEP.includes(dinhDang)) {
                toast.error(`Chỉ hỗ trợ tệp có định dạng ${DINH_DANG_CHO_PHEP.join(', ')}`);
                return Upload.LIST_IGNORE;
            }
            if (file.size > DUNG_LUONG_TOI_DA) {
                toast.error('Tệp mẫu hợp đồng không được vượt quá 20MB');
                return Upload.LIST_IGNORE;
            }
            setFileList([file]);
            return false;
        },
    };

    const onLuu = async () => {
        if (fileList.length === 0) {
            toast.warning('Vui lòng chọn tệp mẫu hợp đồng lao động cần tải lên!');
            return;
        }

        try {
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);

            const rs = await luuCauHinhHopDong(fileList[0]);
            if (rs) {
                toast.success('Lưu cấu hình mẫu hợp đồng lao động thành công!');
                setFileList([]);
                setFileCauHinh(rs);
            } else {
                toast.error('Lưu không thành công!');
            }
        } catch (err: any) {
            toast.error(err?.message || 'Đã xảy ra lỗi, lưu không thành công!');
        }
        dispatch(hideLoadingScreen());
        setIsLoadingBtn(false);
    };

    useEffect(() => {
        layDuLieu();
    }, []);

    const renderFileHienTai = () => {
        if (!fileCauHinh) {
            return <Empty description="Chưa cấu hình tệp mẫu hợp đồng lao động" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        }

        return (
            <Space align="center" wrap>
                <FileWordOutlined style={{ fontSize: 22, color: '#1554ad' }} />
                <Space direction="vertical" size={0}>
                    <Text strong>{fileCauHinh.tenTep}</Text>
                    {fileCauHinh.ngayCapNhat &&
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Cập nhật lúc {dateCommon.formatDate(fileCauHinh.ngayCapNhat, 'dd/MM/yyyy HH:mm')}
                        </Text>}
                </Space>
                <a href={urlFile} download={fileCauHinh.tenTep}>
                    <Button icon={<DownloadOutlined />}>Tải về</Button>
                </a>
            </Space>
        );
    };

    return <section>
        <Tabs
            defaultActiveKey="1"
            items={[
                {
                    key: '1',
                    label: 'Cấu hình mẫu Hợp đồng lao động',
                    children:
                        <div className="card">
                            <div className="card-body">
                                <Row gutter={[16, 16]}>
                                    <Col span={24}>
                                        <Text strong>Tệp mẫu đang sử dụng</Text>
                                        <div style={{ marginTop: 8 }}>{renderFileHienTai()}</div>
                                        {fileCauHinh && fileCauHinh.dinhDang?.toLowerCase() === '.docx' &&
                                            <div style={{ marginTop: 12 }}>
                                                <Text strong>Tham số {'{..}'} có trong tệp mẫu</Text>
                                                <div style={{ marginTop: 6 }}>
                                                    {(fileCauHinh.dsThamSo || []).length === 0
                                                        ? <Text type="secondary">
                                                            Tệp mẫu chưa có tham số nào, hệ thống sẽ không điền tự động được dữ liệu hợp đồng.
                                                        </Text>
                                                        : (fileCauHinh.dsThamSo || []).map((ten) =>
                                                            <Tag key={ten} color="blue" style={{ marginBottom: 4 }}>{`{${ten}}`}</Tag>)}
                                                </div>
                                            </div>}
                                    </Col>
                                    <Col span={24}>
                                        <Text strong>Tải lên tệp mẫu mới</Text>
                                        <div style={{ marginTop: 8 }}>
                                            <Upload {...propsUpload}>
                                                <Button icon={<UploadOutlined />}>Chọn tệp</Button>
                                            </Upload>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Hỗ trợ {DINH_DANG_CHO_PHEP.join(', ')} — tối đa 20MB.
                                                Tệp mới sẽ thay thế tệp mẫu đang sử dụng.
                                            </Text>
                                        </div>
                                    </Col>
                                    <Col span={24} style={{ textAlign: 'right' }}>
                                        <Button type="primary" loading={isLoadingBtn} onClick={onLuu} icon={<SaveOutlined />}>
                                            Lưu Cấu Hình
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                }
            ]}
        />
    </section>
}

export default CauHinhHopDong;
